import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Form, Input, Modal, Radio, Select, TimePicker} from 'antd';
import dayjs from 'dayjs';
import {getHrTemplates} from '@/api/hrTemplate';
import {createRoutinePush, updateRoutinePush} from '@/api/hrPush';
import {DEFAULT_TIME, ROUTINE_TYPES} from '../routineUtils';
import styles from './TaskFormModal.module.less';

export default function TaskFormModal({open, appId, rows, editing, onClose, onSaved}) {
    const [form] = Form.useForm();
    const [templates, setTemplates] = useState([]);
    const [taskType, setTaskType] = useState();
    const [saving, setSaving] = useState(false);
    const [timePickerOpen, setTimePickerOpen] = useState(false);
    const minuteSelected = useRef(false);
    const usedTypes = rows.filter(row => row.id !== editing?.id).map(row => row.taskType);

    useEffect(() => {
        setTimePickerOpen(false);
        if (!open) return;
        // 弹窗是常驻组件，每次打开前必须先清空，否则会残留上一次（新建或编辑）填写的内容
        form.resetFields();
        setTaskType(editing?.taskType);
        form.setFieldsValue(
            editing
                ? {...editing, executeTime: dayjs(editing.executeTime, 'HH:mm')}
                : {
                      taskName: undefined,
                      taskType: undefined,
                      executePeriod: 'daily',
                      executeTime: dayjs(DEFAULT_TIME.BIRTHDAY, 'HH:mm'),
                      templateId: undefined,
                      enabled: false,
                  }
        );
    }, [open, editing, form]);

    useEffect(() => {
        if (!open || !taskType) {
            setTemplates([]);
            return;
        }
        getHrTemplates(appId, {taskType}).then(({data = []}) =>
            setTemplates(data.filter(item => item.enabled))
        );
    }, [open, appId, taskType]);

    const submit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            return;
        }
        setSaving(true);
        try {
            const data = {
                ...values,
                executeTime: values.executeTime.format('HH:mm'),
                executePeriod: 'daily',
            };
            if (editing?.id) await updateRoutinePush(appId, editing.id, data);
            else await createRoutinePush(appId, data);
            onSaved();
        } finally {
            setSaving(false);
        }
    };

    const handleTimePickerOpenChange = nextOpen => {
        minuteSelected.current = false;
        setTimePickerOpen(nextOpen);
    };

    const handleTimeSelect = value => {
        if (!minuteSelected.current) return;
        minuteSelected.current = false;
        form.setFields([{name: 'executeTime', value, errors: []}]);
        setTimePickerOpen(false);
    };

    return (
        <Modal
            open={open}
            title={editing ? '编辑推送' : '新建推送'}
            confirmLoading={saving}
            onOk={submit}
            onCancel={onClose}
        >
            <Form form={form} layout="vertical">
                <Form.Item name="taskName" label="任务名称" rules={[{required: true}]}>
                    <Input placeholder="请输入" />
                </Form.Item>
                <Form.Item name="taskType" label="任务类型" rules={[{required: true}]}>
                    <Select
                        placeholder="请选择"
                        disabled={!!editing}
                        onChange={value => {
                            setTemplates([]);
                            setTaskType(value);
                            form.setFieldValue('executeTime', dayjs(DEFAULT_TIME[value], 'HH:mm'));
                            form.setFieldValue('templateId', undefined);
                        }}
                        options={Object.entries(ROUTINE_TYPES)
                            .filter(([value]) => !usedTypes.includes(value))
                            .map(([value, label]) => ({value, label}))}
                    />
                </Form.Item>
                <Form.Item name="executePeriod" label="执行周期" rules={[{required: true}]}>
                    <Select options={[{value: 'daily', label: '每日'}]} />
                </Form.Item>
                <Form.Item name="executeTime" label="执行时间" rules={[{required: true}]}>
                    <TimePicker
                        format="HH:mm"
                        minuteStep={5}
                        style={{width: '100%'}}
                        hideDisabledOptions
                        showNow={false}
                        open={timePickerOpen}
                        popupClassName={styles.timePickerPopup}
                        onOpenChange={handleTimePickerOpenChange}
                        onSelect={handleTimeSelect}
                        cellRender={(current, info) =>
                            info.subType === 'minute'
                                ? React.cloneElement(info.originNode, {
                                      onClick: () => {
                                          minuteSelected.current = true;
                                      },
                                  })
                                : info.originNode
                        }
                    />
                </Form.Item>
                <Form.Item
                    name="templateId"
                    label="消息模板"
                    rules={[{required: true, message: '请选择消息模板'}]}
                >
                    <Select
                        placeholder="请选择"
                        notFoundContent={
                            templates.length
                                ? '暂无数据'
                                : `该任务类型暂无已启用模板，请先在模板管理中创建并启用`
                        }
                        options={templates.map(({id, templateName}) => ({
                            value: id,
                            label: templateName,
                        }))}
                    />
                </Form.Item>
                <Form.Item name="enabled" label="状态" rules={[{required: true}]}>
                    <Radio.Group
                        options={[
                            {value: true, label: '启用'},
                            {value: false, label: '停用'},
                        ]}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
}

TaskFormModal.propTypes = {
    open: PropTypes.bool.isRequired,
    appId: PropTypes.string.isRequired,
    rows: PropTypes.arrayOf(PropTypes.object),
    editing: PropTypes.shape({
        id: PropTypes.string,
        taskType: PropTypes.string,
        executeTime: PropTypes.string,
    }),
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired,
};

TaskFormModal.defaultProps = {
    rows: [],
    editing: null,
};
